import { useState, useEffect, useId } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";

import { apiAxios } from "@/api/config";
import { useNotification } from "@/providers/NotificationProvider";
import { useAnalytics } from "@/providers/AnalyticsProvider";
import type { AuthUser } from "@/stores/auth";
import { useAuthStore } from "@/stores/auth";
import { useShallow } from "zustand/shallow";

interface AuthResponse {
  user: AuthUser;
  access_token: string;
}
import { inputClass, mutedText } from "@/styles/variants";
import { ui, notifications } from "@/strings";
import { PasswordStrengthIndicator } from "./PasswordStrengthIndicator";
import { buttonVariants } from "@markeroo/theme";
import PiMultipleCrossCancelDefaultStroke from "@markeroo/icons/src/stroke/maths/PiMultipleCrossCancelDefaultStroke";

const logoUrl = new URL("../../images/logo-full.png", import.meta.url).href;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { show } = useNotification();
  const analytics = useAnalytics();
  const { setUser, hydrated, user } = useAuthStore(useShallow((state) => ({
    setUser: state.setUser,
    hydrated: state.hydrated,
    user: state.user,
  })));

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get("token");

  // Generate unique ID for error message accessibility
  const confirmPasswordErrorId = useId();

  useEffect(() => {
    if (hydrated && user) {
      void navigate("/", { replace: true });
      return;
    }

    if (!token) {
      setError(ui.labels.verificationLinkExpired);
    }
  }, [hydrated, user, navigate, token]);

  const passwordsMatch = password === confirmPassword;
  const isPasswordValid =
    password.length >= 12 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /\d/.test(password) &&
    /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password);

  const canSubmit = password && confirmPassword && passwordsMatch && isPasswordValid && token;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      setError(ui.labels.verificationLinkExpired);
      return;
    }

    if (!passwordsMatch) {
      show(ui.labels.passwordsDoNotMatch, "error");
      return;
    }

    if (!isPasswordValid) {
      show(ui.labels.passwordRequirements, "error");
      return;
    }

    setIsSubmitting(true);

    apiAxios
      .post<AuthResponse>("/auth/reset-password", {
        token,
        new_password: password,
      })
      .then((response) => {
        const { user: resetUser } = response.data;
        analytics.track("password_reset_success");
        setUser(resetUser);
        show(notifications.auth.passwordResetSuccess, "success");
        void navigate("/", { replace: true });
      })
      .catch((err: unknown) => {
        analytics.track("password_reset_failed");
        const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail;
        if (detail) {
          setError(detail);
        } else {
          show(notifications.auth.passwordResetInvalid, "error");
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };


  if (error && !token) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 py-10">
        <div className="flex w-full max-w-md flex-col items-center gap-6 rounded-2xl bg-white p-8 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
          <img src={logoUrl} alt="Markeroo logo" className="h-20 w-auto" />

          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-semantic-error-bg">
            <PiMultipleCrossCancelDefaultStroke className="h-6 w-6 text-semantic-error" aria-hidden="true" />
          </div>

          <h1 className="text-xl font-semibold text-text-theme-primary">{ui.actions.resetPassword}</h1>
          <p className="text-center text-sm text-semantic-error">{error}</p>

          <Link
            to="/forgot-password"
            className="mt-2 font-medium text-primary hover:text-primary-hover"
          >
            {ui.actions.sendResetLink}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 py-10">
      <div className="flex w-full max-w-md flex-col gap-6 rounded-2xl bg-white p-8 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
        <div className="flex flex-col items-center gap-2">
          <img src={logoUrl} alt="Markeroo logo" className="h-20 w-auto" />
          <h1 className="text-xl font-semibold text-text-theme-primary">{ui.actions.resetPassword}</h1>
          <p className={mutedText}>{ui.labels.enterNewPassword}</p>
        </div>

        {error && (
          <div className="rounded-lg bg-semantic-error-bg p-3 text-center text-sm text-semantic-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-text-theme-secondary">
              {ui.labels.newPassword}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`${inputClass} mt-1`}
              required
              disabled={isSubmitting}
              autoComplete="new-password"
            />
            <PasswordStrengthIndicator password={password} />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-theme-secondary">
              {ui.labels.confirmPassword}
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={`${inputClass} mt-1 ${
                confirmPassword && !passwordsMatch ? "border-semantic-error" : ""
              }`}
              required
              disabled={isSubmitting}
              autoComplete="new-password"
              aria-describedby={confirmPassword && !passwordsMatch ? confirmPasswordErrorId : undefined}
              aria-invalid={confirmPassword && !passwordsMatch ? true : undefined}
            />
            {confirmPassword && !passwordsMatch && (
              <p id={confirmPasswordErrorId} className="mt-1 text-xs text-semantic-error" role="alert">
                {ui.labels.passwordsDoNotMatch}
              </p>
            )}
          </div>

          <button
            type="submit"
            className={buttonVariants.primary}
            disabled={isSubmitting || !canSubmit}
          >
            {isSubmitting ? ui.actions.resettingPassword : ui.actions.resetPassword}
          </button>
        </form>

        <div className="text-center">
          <Link to="/login" className="text-sm font-medium text-primary hover:text-primary-hover">
            {ui.actions.backToLogin}
          </Link>
        </div>
      </div>
    </div>
  );
}
